import { useNavigate, useLocation } from 'react-router-dom'
import { BarChart3, Bug, FileDown, FolderOpen, ShieldCheck } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useProjectContext } from '@/lib/context/ProjectContext'

interface ResultsLayoutProps {
    children: React.ReactNode
}

export default function ResultsLayout({ children }: ResultsLayoutProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const { selectedProject } = useProjectContext()

    const getActiveTab = () => {
        if (location.pathname.includes('/results/test-results')) return 'test-results'
        if (location.pathname.includes('/results/reports')) return 'reports'
        if (location.pathname.includes('/results/advanced-reports')) return 'advanced-reports'
        if (location.pathname.includes('/results/defects')) return 'defects'
        return 'reports'
    }

    const handleTabChange = (value: string) => {
        navigate(`/results/${value}`)
    }

    return (
        <div className="p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold">Evidence Ops</h1>
                <p className="text-muted-foreground mt-1">
                    {selectedProject ? (
                        <>
                            Governed evidence, reports, test results, and defects for{' '}
                            <span className="font-medium text-foreground">{selectedProject.name}</span>
                        </>
                    ) : (
                        'Select a project to view governed reports, operational evidence, test results, and defects'
                    )}
                </p>
            </div>

            <Tabs value={getActiveTab()} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full max-w-5xl grid-cols-4">
                    <TabsTrigger value="reports">
                        <ShieldCheck className="h-4 w-4 mr-2" />
                        Evidence Reports
                    </TabsTrigger>
                    <TabsTrigger value="test-results">
                        <BarChart3 className="h-4 w-4 mr-2" />
                        Test Results
                    </TabsTrigger>
                    <TabsTrigger value="advanced-reports">
                        <FileDown className="h-4 w-4 mr-2" />
                        Analytics
                    </TabsTrigger>
                    <TabsTrigger value="defects">
                        <Bug className="h-4 w-4 mr-2" />
                        Defects
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {!selectedProject ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20">
                    <FolderOpen className="h-12 w-12 text-gray-300" />
                    <h3 className="mt-4 text-lg font-semibold text-gray-600">No Project Selected</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Select a project from the header to continue.
                    </p>
                </div>
            ) : (
                children
            )}
        </div>
    )
}
